import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  SearchIcon,
  CreditCardIcon,
  TrendingUpIcon,
  Banknote as BanknoteIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DownloadIcon,
  RefreshCwIcon,
  FilterIcon,
  CalendarIcon,
  BuildingIcon,
  FileTextIcon,
  AlertCircleIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraBilling = () => {
  const [transactions, setTransactions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const transactionsPerPage = 15;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch companies
      const companiesResponse = await axios.get(`${BACKEND_URL}/api/ultra-admin/tenants`, config);
      setCompanies(companiesResponse.data.tenants);

      // Fetch transactions
      // For now, we'll create mock data since we need a backend endpoint
      const mockTransactions = generateMockTransactions(companiesResponse.data.tenants);
      setTransactions(mockTransactions);

    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Mock transaction generator (replace with real API later)
  const generateMockTransactions = (companies) => {
    const transactions = [];
    const statuses = ['completed', 'pending', 'failed', 'refunded'];
    const methods = ['credit_card', 'bank_transfer', 'paypal'];
    
    companies.forEach((company, index) => {
      // Generate 1-3 transactions per company
      const txCount = Math.floor(Math.random() * 3) + 1;
      for (let i = 0; i < txCount; i++) {
        const daysAgo = Math.floor(Math.random() * 30);
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        
        transactions.push({
          id: `tx_${Date.now()}_${index}_${i}`,
          company_id: company.id,
          company_name: company.company_name,
          amount: company.monthly_fee || 29,
          currency: 'TRY',
          status: statuses[Math.floor(Math.random() * statuses.length)],
          payment_method: methods[Math.floor(Math.random() * methods.length)],
          description: `${company.subscription_plan} subscription`,
          invoice_number: `INV-2025-${String(1000 + index + i).padStart(4, '0')}`,
          created_at: date.toISOString(),
          package_name: company.subscription_plan
        });
      }
    });

    return transactions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Calculate stats
  const calculateStats = () => {
    const completed = transactions.filter(t => t.status === 'completed');
    const pending = transactions.filter(t => t.status === 'pending');
    const failed = transactions.filter(t => t.status === 'failed');
    
    const totalRevenue = completed.reduce((sum, t) => sum + t.amount, 0);
    const pendingAmount = pending.reduce((sum, t) => sum + t.amount, 0);
    
    // Calculate MRR (Monthly Recurring Revenue)
    const activeSubscriptions = companies.filter(c => c.status === 'active' || c.status === 'trial');
    const mrr = activeSubscriptions.reduce((sum, c) => sum + (c.monthly_fee || 0), 0);

    return {
      totalRevenue,
      pendingAmount,
      mrr,
      totalTransactions: transactions.length,
      completedCount: completed.length,
      pendingCount: pending.length,
      failedCount: failed.length,
      successRate: transactions.length > 0 ? ((completed.length / transactions.length) * 100).toFixed(1) : 0
    };
  };

  const stats = calculateStats();

  // Filter transactions
  const getFilteredTransactions = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(t => 
        (t.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Company filter
    if (companyFilter !== 'all') {
      filtered = filtered.filter(t => t.company_id === companyFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(t => {
        const txDate = new Date(t.created_at);
        const diffDays = Math.floor((now - txDate) / (1000 * 60 * 60 * 24));
        
        switch (dateFilter) {
          case 'today':
            return diffDays === 0;
          case 'week':
            return diffDays <= 7;
          case 'month':
            return diffDays <= 30;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();

  // Pagination
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  const getStatusBadge = (status) => {
    const config = {
      completed: { label: 'Tamamlandı', className: 'bg-green-100 text-green-700', icon: CheckCircleIcon },
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-700', icon: ClockIcon },
      failed: { label: 'Başarısız', className: 'bg-red-100 text-red-700', icon: XCircleIcon },
      refunded: { label: 'İade Edildi', className: 'bg-gray-100 text-gray-700', icon: AlertCircleIcon }
    };
    
    const statusConfig = config[status] || config.pending;
    const Icon = statusConfig.icon;
    
    return (
      <Badge className={`${statusConfig.className} flex items-center space-x-1`}>
        <Icon className="w-3 h-3" />
        <span>{statusConfig.label}</span>
      </Badge>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      credit_card: '💳',
      bank_transfer: '🏦',
      paypal: '💰'
    };
    return icons[method] || '💳';
  };

  const handleShowDetail = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCwIcon className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Faturalandırma verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Faturalandırma & Ödemeler</h2>
            <p className="text-gray-600 mt-1">Tüm ödeme işlemlerini ve faturaları yönetin</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <DownloadIcon className="w-4 h-4 mr-2" />
              Rapor İndir
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <BanknoteIcon className="w-6 h-6" />
              </div>
              <TrendingUpIcon className="w-6 h-6 text-emerald-200" />
            </div>
            <p className="text-emerald-100 text-sm font-medium">Toplam Gelir</p>
            <p className="text-3xl font-bold mt-1">₺{stats.totalRevenue.toFixed(2)}</p>
            <p className="text-emerald-100 text-xs mt-2">{stats.completedCount} tamamlanmış işlem</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <TrendingUpIcon className="w-6 h-6" />
              </div>
              <CalendarIcon className="w-6 h-6 text-blue-200" />
            </div>
            <p className="text-blue-100 text-sm font-medium">Aylık Gelir (MRR)</p>
            <p className="text-3xl font-bold mt-1">₺{stats.mrr.toFixed(2)}</p>
            <p className="text-blue-100 text-xs mt-2">Aktif abonelikler</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <ClockIcon className="w-6 h-6" />
              </div>
            </div>
            <p className="text-orange-100 text-sm font-medium">Bekleyen Ödemeler</p>
            <p className="text-3xl font-bold mt-1">₺{stats.pendingAmount.toFixed(2)}</p>
            <p className="text-orange-100 text-xs mt-2">{stats.pendingCount} bekleyen işlem</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6" />
              </div>
            </div>
            <p className="text-purple-100 text-sm font-medium">Başarı Oranı</p>
            <p className="text-3xl font-bold mt-1">%{stats.successRate}</p>
            <p className="text-purple-100 text-xs mt-2">{stats.failedCount} başarısız işlem</p>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2 relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Şirket veya fatura no ara..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">📊 Tüm Durumlar</option>
            <option value="completed">✅ Tamamlandı</option>
            <option value="pending">⏳ Beklemede</option>
            <option value="failed">❌ Başarısız</option>
            <option value="refunded">↩️ İade Edildi</option>
          </select>

          <select
            value={companyFilter}
            onChange={(e) => {
              setCompanyFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">🏢 Tüm Şirketler</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.company_name}</option>
            ))}
          </select>

          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">📅 Tüm Zamanlar</option>
            <option value="today">Bugün</option>
            <option value="week">Son 7 Gün</option>
            <option value="month">Son 30 Gün</option>
          </select>
        </div>

        <div className="text-xs text-gray-500 mt-3">
          {filteredTransactions.length} işlem gösteriliyor
        </div>
      </Card>

      {/* Transactions Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fatura No / Şirket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tutar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ödeme Yöntemi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentTransactions.map(transaction => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{transaction.invoice_number}</p>
                      <p className="text-sm text-gray-500">{transaction.company_name}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className="capitalize">
                      {transaction.package_name}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-gray-900">
                      ₺{transaction.amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">{transaction.currency}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span>{getPaymentMethodIcon(transaction.payment_method)}</span>
                      <span className="text-sm text-gray-600 capitalize">
                        {transaction.payment_method.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(transaction.created_at).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleShowDetail(transaction)}
                      >
                        <FileTextIcon className="w-4 h-4 mr-1" />
                        Detay
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTransactions.length === 0 && (
          <div className="text-center py-12">
            <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">İşlem bulunamadı</h3>
            <p className="text-gray-500">Arama kriterlerinize uygun işlem yok.</p>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {indexOfFirstTransaction + 1}-{Math.min(indexOfLastTransaction, filteredTransactions.length)} / {filteredTransactions.length} işlem
            </p>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Önceki
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                    return false;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <Button
                        size="sm"
                        variant={currentPage === page ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? 'bg-purple-600' : ''}
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">İşlem Detayları</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Fatura Numarası</p>
                    <p className="text-2xl font-bold text-purple-900">{selectedTransaction.invoice_number}</p>
                  </div>
                  {getStatusBadge(selectedTransaction.status)}
                </div>
              </div>

              {/* Company Info */}
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Şirket Bilgileri</h4>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BuildingIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedTransaction.company_name}</p>
                    <p className="text-sm text-gray-500">ID: {selectedTransaction.company_id}</p>
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">İşlem Detayları</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Paket</p>
                    <p className="font-medium text-gray-900 capitalize">{selectedTransaction.package_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Tutar</p>
                    <p className="font-medium text-gray-900">₺{selectedTransaction.amount.toFixed(2)} {selectedTransaction.currency}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ödeme Yöntemi</p>
                    <p className="font-medium text-gray-900 capitalize">
                      {getPaymentMethodIcon(selectedTransaction.payment_method)} {selectedTransaction.payment_method.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">İşlem Tarihi</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedTransaction.created_at).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">İşlem ID</p>
                    <p className="font-mono text-xs text-gray-900">{selectedTransaction.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Açıklama</p>
                    <p className="text-sm text-gray-900">{selectedTransaction.description}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t">
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700">
                  <DownloadIcon className="w-4 h-4 mr-2" />
                  Faturayı İndir (PDF)
                </Button>
                <Button variant="outline" className="flex-1">
                  <FileTextIcon className="w-4 h-4 mr-2" />
                  E-posta Gönder
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraBilling;
